window.BAO.MediaBreakpoints = class MediaBreakpoints {
  static get breakpoints () {
    return {
      xs: 450,
      sm: 768,
      md: 900,
      lg: 1200,
      xlg: 1440,
      xxlg: 1600,
    }
  }

  static get isXxs () {
    return window.innerWidth > this.breakpoints.xs
  }

  static get isXs () {
    return window.innerWidth > this.breakpoints.xs
  }

  static get isSm () {
    return window.innerWidth >= this.breakpoints.sm
  }

  static get isMd () {
    return window.innerWidth > this.breakpoints.md
  }

  static get isLg () {
    return window.innerWidth > this.breakpoints.lg
  }

  static get isXlg () {
    return window.innerWidth > this.breakpoints.xlg
  }

  static get isXxlg () {
    return window.innerWidth > this.breakpoints.xxlg
  }

  static get isXxsViewport () {
    return window.innerWidth <= this.breakpoints.xs
  }

  static get isXsViewport () {
    return window.innerWidth < this.breakpoints.sm
  }

  static get isSmViewport () {
    return (
      this.breakpoints.sm <= window.innerWidth &&
      window.innerWidth <= this.breakpoints.md
    )
  }

  static get isMdViewport () {
    return (
      this.breakpoints.md < window.innerWidth &&
      window.innerWidth <= this.breakpoints.lg
    )
  }

  static get isLgViewport () {
    return (
      this.breakpoints.lg < window.innerWidth &&
      window.innerWidth <= this.breakpoints.xlg
    )
  }
}

/**
 * ViewportManager monitors window resize events and dispatches custom event
 * when the viewport transitions between mobile and desktop breakpoints.
 *
 * The mobile/desktop threshold is at 768px (sm breakpoint).
 *
 * Events dispatched:
 * - VIEWPORT.CHANGE: Fires on any mobile/desktop transition
 *
 * Example usage in a custom element:
 *
 * setupListeners() {
 *   super.setupListeners()
 *
 *   this.onViewportChange = this.onViewportChange.bind(this)
 *   window.BAO.eventBus.addEventListener(
 *     window.BAO.EVENTS.VIEWPORT.CHANGE,
 *     this.onViewportChange
 *   )
 * }
 *
 * onViewportChange({ detail }) {
 *   // detail.isDesktop - boolean indicating if now desktop
 *   // detail.wasDesktop - boolean indicating if previously desktop
 *   // detail.wasMobile - boolean indicating if previously mobile
 *   // detail.previousBreakpoint - previous breakpoint name
 *   // detail.currentBreakpoint - current breakpoint name
 *
 *   if (detail.isDesktop) {
 *     this.initDesktopFunctionality()
 *   } else {
 *     this.initMobileFunctionality()
 *   }
 * }
 *
 * disconnectedCallback() {
 *   super.disconnectedCallback()
 *
 *   window.BAO.eventBus.removeEventListener(
 *     window.BAO.EVENTS.VIEWPORT.CHANGE,
 *     this.onViewportChange
 *   )
 * }
 */
window.BAO.ViewportManager = class ViewportManager {
  constructor () {
    this.currentBreakpoint = this.getCurrentBreakpoint()
    this.isDesktop = this.checkIsDesktop()

    this.handleResize = window.BAO.Utils.debounce(
      this.onResize.bind(this),
      250,
    )

    window.addEventListener('resize', this.handleResize)
  }

  getCurrentBreakpoint () {
    const width = window.innerWidth
    const breakpoints = window.BAO.MediaBreakpoints.breakpoints

    if (width <= breakpoints.xs) return 'xxs'
    if (width < breakpoints.sm) return 'xs'
    if (width <= breakpoints.md) return 'sm'
    if (width <= breakpoints.lg) return 'md'
    if (width <= breakpoints.xlg) return 'lg'
    if (width <= breakpoints.xxlg) return 'xlg'
    return 'xxlg'
  }

  checkIsDesktop () {
    return window.innerWidth >= window.BAO.MediaBreakpoints.breakpoints.sm
  }

  onResize () {
    const newBreakpoint = this.getCurrentBreakpoint()
    const newIsDesktop = this.checkIsDesktop()

    if (this.isDesktop !== newIsDesktop) {
      const wasDesktop = this.isDesktop
      this.isDesktop = newIsDesktop

      window.BAO.dispatchEvent(window.BAO.EVENTS.VIEWPORT.CHANGE, {
        isDesktop: newIsDesktop,
        wasMobile: wasDesktop === false,
        wasDesktop,
        previousBreakpoint: this.currentBreakpoint,
        currentBreakpoint: newBreakpoint,
      })
    }

    this.currentBreakpoint = newBreakpoint
  }

  destroy () {
    window.removeEventListener('resize', this.handleResize)
  }
}
